import axios from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context';
import { getSdkManager } from '@/sdkManagerSetup';

const REFRESH_BUFFER_SECONDS = 10; // Refresh token 10 seconds before expiry

/**
 * Parse JWT token and extract expiry time.
 * @param {string} token - JWT token
 * @returns {number|null} - Expiry timestamp in seconds, or null if invalid
 */
const getTokenExpiry = token => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp || null;
  } catch {
    return null;
  }
};

/**
 * Check if token is about to expire (within REFRESH_BUFFER_SECONDS).
 * @param {string} token - JWT token
 * @returns {boolean} - True if token needs refresh
 */
const isTokenExpiringSoon = token => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true; // If we can't parse, assume expired
  const nowSeconds = Math.floor(Date.now() / 1000);
  return expiry - nowSeconds <= REFRESH_BUFFER_SECONDS;
};

/**
 * Get current token from SDK (static property).
 */
const getCurrentToken = () => {
  const sdkManager = getSdkManager();
  const loginService = sdkManager?.loginService || window.wbSdk?.loginService;
  return loginService?.accessToken || null;
};

/**
 * Force refresh token using SDK's getAccessToken() method.
 * This method checks expiry internally and refreshes if needed.
 */
const refreshToken = async () => {
  const sdkManager = getSdkManager();
  const loginService = sdkManager?.loginService || window.wbSdk?.loginService;

  if (!loginService) {
    return null;
  }

  try {
    if (typeof loginService.getAccessToken === 'function') {
      return await loginService.getAccessToken();
    }
  } catch (err) {
    console.warn('Token refresh failed:', err);
  }

  return loginService.accessToken || null;
};

/**
 * Get a valid token, refreshing only if about to expire.
 * @returns {Promise<string|null>} - Valid access token or null
 */
const getValidToken = async () => {
  const currentToken = getCurrentToken();

  // If token is still valid, use it
  if (currentToken && !isTokenExpiringSoon(currentToken)) {
    return currentToken;
  }

  // Token expired or expiring soon, refresh it
  console.debug('Token expiring soon or expired, refreshing...');
  return await refreshToken();
};

/**
 * useAIProvider - Reusable Hook for AI Provider
 *
 * Features:
 * - Proactive token refresh: Refreshes 10 seconds before expiry
 * - Reactive retry: On 401/403, refreshes token and retries once
 *
 * @param {object} defaultOptions - Default configuration for API calls
 * @returns {object} Methods and state for using AI Provider API
 */
export const useAIProvider = (defaultOptions = {}) => {
  const { jwtToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const abortControllerRef = useRef(null);

  const config = useMemo(
    () => ({
      temperature: defaultOptions.temperature ?? 0.7,
      maxTokens: defaultOptions.maxTokens || 1000,
    }),
    [defaultOptions]
  );

  const baseURL = useMemo(() => window.wideBridgeConfig?.aiProvider?.baseURL, []);
  const isConfigured = !!baseURL && !!jwtToken;

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort?.();
      abortControllerRef.current.cancel?.();
      abortControllerRef.current = null;
      setLoading(false);
    }
  }, []);

  /**
   * Make an API request with automatic token refresh on 401/403.
   * @param {Function} requestFn - Function that takes a token and returns axios promise
   * @returns {Promise<any>} - Response data
   */
  const executeWithTokenRefresh = useCallback(async requestFn => {
    // Get a valid token (refreshes if expiring soon)
    let token = await getValidToken();
    if (!token) {
      throw new Error('Unable to obtain access token. Please re-login.');
    }

    try {
      return await requestFn(token);
    } catch (err) {
      // Don't retry if the error is a server-side API key issue (e.g. expired Bedrock key)
      if (err.response?.data?.type === 'expired_api_key') {
        throw err;
      }
      // On 401/403, try refreshing token and retry once
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.debug('Auth error, attempting token refresh and retry...');
        token = await refreshToken();
        if (!token) {
          throw new Error('Token refresh failed. Please re-login.');
        }
        return await requestFn(token);
      }
      throw err;
    }
  }, []);

  /**
   * Base (non-streaming) API call with smart token refresh.
   */
  const callAPI = useCallback(
    async (messages, options = {}) => {
      if (!baseURL) {
        const errMsg = 'AI Provider not ready. Check configuration.';
        setError(errMsg);
        throw new Error(errMsg);
      }

      setLoading(true);
      setError(null);
      setResponse(null);

      const source = axios.CancelToken.source();
      abortControllerRef.current = source;

      try {
        const data = await executeWithTokenRefresh(async token => {
          const res = await axios.post(
            `${baseURL}/chat`,
            {
              messages,
              temperature: options.temperature ?? config.temperature,
              max_tokens: options.maxTokens || config.maxTokens,
            },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              cancelToken: source.token,
            }
          );
          return res.data;
        });

        let content = data?.message || '';
        if (typeof content === 'object' && content !== null) {
          content = JSON.stringify(content);
        }

        setResponse(content);
        return content;
      } catch (err) {
        let message;
        if (axios.isCancel(err)) {
          message = 'Request cancelled';
        } else if (err.response?.data?.type === 'expired_api_key') {
          message = 'AI service API key has expired. Please contact your administrator to rotate the key.';
        } else {
          message = err.response?.data?.error || err.message || 'AI API request failed';
        }
        console.error('[AI Provider]', message, err.response?.status, err.response?.data);
        setError(message);
        throw err;
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [baseURL, config, executeWithTokenRefresh]
  );

  /**
   * Audio Transcription with smart token refresh.
   * @param {File|Blob} audioFile - Audio file to transcribe
   * @param {object} options - Optional config (model, language, etc.)
   * @returns {Promise<string>} - Transcribed text
   */
  const callTranscribeAudio = useCallback(
    async (audioFile, options = {}) => {
      if (!baseURL) {
        const errMsg = 'AI Provider not ready. Check configuration.';
        setError(errMsg);
        throw new Error(errMsg);
      }

      setLoading(true);
      setError(null);
      setResponse(null);

      const formData = new FormData();
      formData.append('file', audioFile);
      formData.append('model', options.model || 'whisper-1');
      if (options.prompt) formData.append('prompt', options.prompt);
      if (options.language) formData.append('language', options.language);
      if (options.response_format) formData.append('response_format', options.response_format);

      try {
        const data = await executeWithTokenRefresh(async token => {
          const res = await axios.post(`${baseURL}/audio/transcriptions`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
          });
          return res.data;
        });

        const text = data.text || '';
        setResponse(text);
        return text;
      } catch (err) {
        const message = err.response?.data?.error?.message || err.message || 'Audio transcription failed';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [baseURL, executeWithTokenRefresh]
  );

  return {
    ready: isConfigured,
    loading,
    error,
    response,
    callAPI,
    callTranscribeAudio,
    cancelRequest,
  };
};
